import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import tradeDataStore from "@/store/tradeDataStore";
import {
  isFillCancelled,
  isFillLocked,
  isFillCellsEditable,
  getFillFromStore,
} from "../../fillStateHelpers";
import { getLegSummaryDataFromStore } from "../FillsSummary/fillsSummaryHelpers";
import { NotationToDecimalValueType } from "@/lib/interfaces and Enums/TYPENotations";

/**
 * Checks if the Fill Price cell should be editable.
 * Editable when:
 * - Trade role is "RP"
 * - Fill is NOT cancelled
 * - Fill is NOT locked (has timestamp) OR is in "Updated (Pending)" state
 *
 * @param params - AG Grid cell params
 * @returns boolean indicating if the cell should be editable
 */
export function isFillPriceCellEditable(params: any): boolean {
  if (!params.data) {
    return false;
  }

  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);

  if (!fullTradeData) {
    return false;
  }

  // Only RP role can edit fill prices
  if (fullTradeData.role !== "RP") {
    return false;
  }

  // Get the fill from the store to check its state
  const esid = fullTradeData.esid;
  const blockId = params.data.blockId;
  const fillId = params.data.fillId;
  const extFillId = params.data.fillData?.extFillId;

  if (!esid || blockId === undefined || fillId === undefined) {
    return false;
  }

  const tradeData = tradeDataStore.getState().tradeData;
  const fill = getFillFromStore(tradeData, esid, blockId, fillId, extFillId);

  if (!fill) {
    return false;
  }

  // Cancelled fills are not editable
  if (isFillCancelled(fill)) {
    return false;
  }

  // Locked fills (with timestamp) are not editable unless in "Updated (Pending)" state
  if (isFillLocked(fill)) {
    return false;
  }

  // Check if fill is in an editable state (New or Updated)
  return isFillCellsEditable(fill);
}

/**
 * Gets the current fill price value from the Zustand store
 * for the specified fillId within a trade's legs fills array.
 *
 * @param esid - The execution strategy ID
 * @param fillId - The fill ID to find
 * @param blockId - The block/leg ID
 * @returns The fill price value or null
 */
export function getFillPriceFromStore(
  esid: string,
  fillId: string | number,
  blockId: number,
): string | undefined {
  const tradeData = tradeDataStore.getState().tradeData;
  const trade = tradeData.find((t) => t.esid === esid);

  if (!trade) {
    return undefined;
  }

  // For RP trades, fills are in the legs array
  if (trade.role === "RP" && trade.legs) {
    const leg = trade.legs.find((l) => l.blockId === blockId);
    if (leg?.fills) {
      const fill = leg.fills.find((f) => f.fillId === fillId);
      return fill?.fillRate;
    }
  }

  // For OP trades, fills are in executionRequestCounterParties > legs
  if (trade.role === "OP" && trade.executionRequestCounterParties) {
    for (const cp of trade.executionRequestCounterParties) {
      if (cp.legs) {
        const leg = cp.legs.find((l) => l.blockId === blockId);
        if (leg?.fills) {
          const fill = leg.fills.find((f) => f.fillId === fillId);
          if (fill) {
            return fill.fillRate;
          }
        }
      }
    }
  }

  return undefined;
}

/**
 * Updates the fill price in the Zustand store.
 * - For "New" fills: keeps fillType as "New", no historic values needed
 * - For unchanged/backend fills: sets fillType to "Updated" and preserves historic values
 * - For already "Updated" fills: keeps fillType, doesn't overwrite historic values
 *
 * @param esid - The execution strategy ID
 * @param fillId - The fill ID to update
 * @param blockId - The block/leg ID
 * @param newPrice - The new price value
 */
export function updateFillPriceInStore(
  esid: string,
  fillId: string | number,
  blockId: number,
  newPrice: number | string | undefined,
): void {
  const tradeData = tradeDataStore.getState().tradeData;
  const tradeIndex = tradeData.findIndex((t) => t.esid === esid);

  if (tradeIndex === -1) {
    return;
  }

  const trade = tradeData[tradeIndex];

  // Only RP can edit fills
  if (trade.role !== "RP" || !trade.legs) {
    return;
  }

  const newLegs = trade.legs.map((leg) => {
    if (leg.blockId !== blockId || !leg.fills) {
      return leg;
    }

    const newFills = leg.fills.map((fill) => {
      if (fill.fillId === fillId) {
        // Convert to string for storage (handles both number and $fr string values)
        const newRateStr =
          newPrice !== undefined ? String(newPrice) : fill.fillRate;

        // New fills stay as "New", just update the value
        if (fill.fillType === "New") {
          return { ...fill, fillRate: newRateStr };
        }

        // For fills from backend (unchanged or already updated):
        // - Set fillType to "Updated"
        // - Preserve historic values if not already set
        return {
          ...fill,
          fillRate: newRateStr,
          fillType: "Updated" as const,
          // Only set historic value if not already preserved
          historicFillRate:
            fill.historicFillRate !== undefined
              ? fill.historicFillRate
              : fill.fillRate,
          historicFillSize:
            fill.historicFillSize !== undefined
              ? fill.historicFillSize
              : fill.fillSize,
        };
      }
      return fill;
    });

    return { ...leg, fills: newFills };
  });

  const newTrade = { ...trade, legs: newLegs };
  const newTradeData = tradeData.map((t, i) =>
    i === tradeIndex ? newTrade : t,
  );

  tradeDataStore.getState().updateTradeData(newTradeData);
}

/**
 * Determines styling based on whether the cell is editable (RP role).
 *
 * @param isRP - Whether the trade role is RP
 * @param theme - Current theme (light/dark)
 * @returns CSS class string for styling
 */
export function getFillPriceCellStyling(
  isRP: boolean,
  theme: string | undefined,
): string {
  if (!isRP) {
    return "";
  }

  const safeTheme = theme ?? "light";
  return safeTheme === "light" ? "inputCellLight" : "inputCellDark";
}

/**
 * Gets the notation from the Zustand store for a given leg.
 * Uses getLegSummaryDataFromStore to avoid duplicating leg-finding logic.
 *
 * @param esid - The execution strategy ID
 * @param blockId - The block/leg ID
 * @returns The notation string (e.g., "bps", "%") or undefined
 */
export function getNotationFromStore(
  esid: string,
  blockId: number,
): NotationToDecimalValueType | undefined {
  const legData = getLegSummaryDataFromStore(esid, blockId);
  return legData.notation;
}

